import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { of, Subject } from 'rxjs';
import { debounceTime, map, switchMap, takeUntil } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { AppStateInterface } from 'src/app/stores/global/appState.interface';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee.store';
import { PermissionStorageService } from 'src/app/authentication/storages/permission-storage.service';
import { ExpenseStoreService } from '../../storage/expense-store.service';
import { ExpenseService } from '../../services/expense.service';
import { ExpenseTypeService } from '../../services/expense-type.service';
import { AssignmentService } from 'src/app/assignment/services/assignment.service';
import { EXPENSES_APPROVE_SELF_PERMISSION, ExpenseStatus, IExpenseDetails } from '../../models/expense';
import { ExpenseTypeKind, IExpenseType } from '../../models/expense-type';

export interface IAssignmentOption {
  id: string;
  title: string;
}

@Component({
    selector: 'app-expense-dialog',
    templateUrl: './expense-dialog.component.html',
    standalone: false
})
export class ExpenseDialogComponent implements OnInit, OnDestroy {

  /** Existing expense to view/edit. Null => create mode. */
  @Input() expenseId: string | null = null;
  /** Whether the form is editable. False => read-only view. */
  @Input() editable = false;
  /** When true, a reviewer may change an already-processed expense (accept/reject/return). */
  @Input() allowReprocess = false;
  /** When true, a reviewer may decide (accept/reject) a still-submitted expense from this popup. */
  @Input() allowDecide = false;
  /** Create mode only: assignment preselected in the combobox (e.g. opened from the assignment's expenses tab). */
  @Input() presetAssignment: IAssignmentOption | null = null;
  @Output() close = new EventEmitter<void>();
  /** Emitted after the expense was saved, submitted or re-decided, so the parent list can reload. */
  @Output() changed = new EventEmitter<void>();

  // reason sub-dialog state (reject)
  reasonOpen = false;

  // expose enum for template label lookup
  ExpenseStatus = ExpenseStatus;

  expense?: IExpenseDetails;
  expenseTypes: IExpenseType[] = [];
  formGroup!: UntypedFormGroup;
  currentId: string | null = null;

  // assignment search (optional link)
  assignments: IAssignmentOption[] = [];
  assignmentsLoading = false;
  private assignmentFilter$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  // Current user's employee id — used to hide reprocess actions on the user's own expense.
  private currentEmployeeId: string | null = null;

  // May the current user decide on their own expenses?
  private hasApproveSelf = (this.permissionStorage.getPermissions() ?? [])
    .includes(EXPENSES_APPROVE_SELF_PERMISSION);

  constructor(
    private expenseStore: ExpenseStoreService,
    private expenseService: ExpenseService,
    private expenseTypeService: ExpenseTypeService,
    private assignmentService: AssignmentService,
    private store: Store<AppStateInterface>,
    private permissionStorage: PermissionStorageService
  ) { }

  ngOnInit(): void {
    this.currentId = this.expenseId;
    this.buildForm();
    this.setupAssignmentSearch();

    this.store.select(currentEmployeeSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe(employee => this.currentEmployeeId = employee?.id ?? null);

    if (this.editable) {
      this.expenseTypeService.searchExpenseTypes().subscribe(response => {
        this.expenseTypes = response.data;
      });
    }

    if (!this.expenseId && this.presetAssignment) {
      // Seed the combobox so the preselected assignment's title renders.
      this.assignments = [this.presetAssignment];
      this.formGroup.patchValue({ linkedAssignmentId: this.presetAssignment.id });
    }

    if (this.expenseId) {
      this.expenseService.getExpense(this.expenseId).subscribe(details => {
        // history newest first
        details.expenseHistory = (details.expenseHistory ?? [])
          .slice()
          .sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());
        this.expense = details;

        // Seed the combobox with the currently linked assignment so its title renders.
        if (details.linkedAssignmentId && details.linkedAssignmentTitle) {
          this.assignments = [{ id: details.linkedAssignmentId, title: details.linkedAssignmentTitle }];
        }

        this.formGroup.patchValue({
          expenseTypeId: details.expenseTypeId,
          description: details.description,
          km: details.km,
          amount: details.amount,
          linkedAssignmentId: details.linkedAssignmentId,
          date: details.date ? new Date(details.date) : null
        });
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildForm(): void {
    this.formGroup = new UntypedFormGroup({
      expenseTypeId: new UntypedFormControl(null, Validators.required),
      description: new UntypedFormControl(null),
      km: new UntypedFormControl(null),
      amount: new UntypedFormControl(null),
      linkedAssignmentId: new UntypedFormControl(null),
      // Pre-filled with today; the user may override it. Overwritten by the loaded value in edit mode.
      date: new UntypedFormControl(new Date(), Validators.required)
    });
  }

  /** Serialize a picked Date as a timezone-agnostic date-only string (yyyy-MM-dd). */
  private toDateOnly(date: Date | null): string | null {
    if (!date) {
      return null;
    }
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  private setupAssignmentSearch(): void {
    this.assignmentFilter$.pipe(
      debounceTime(300),
      switchMap(keyword => {
        if (!keyword || keyword.length < 2) {
          this.assignmentsLoading = false;
          return of([] as IAssignmentOption[]);
        }
        this.assignmentsLoading = true;
        return this.assignmentService.searchAssignments({
          pageNumber: 1,
          pageSize: 20,
          orderBy: ['title'],
          keyword
        }).pipe(
          map(response => (response.data ?? []).map(a => ({ id: a.id, title: a.title })))
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(options => {
      this.assignments = options;
      this.assignmentsLoading = false;
    });
  }

  onAssignmentFilter(keyword: string): void {
    this.assignmentFilter$.next(keyword);
  }

  get dialogTitle(): string {
    if (!this.editable) {
      return 'detailTitle';
    }
    return this.currentId ? 'edit' : 'create';
  }

  get selectedType(): IExpenseType | undefined {
    return this.expenseTypes.find(t => t.id === this.formGroup?.value.expenseTypeId);
  }

  /**
   * Whether the relevant expense type requires a receipt — drives upload visibility.
   * In edit/create mode this follows the currently selected type; in the read-only view it
   * uses the loaded expense's type (expenseTypes are only fetched when editing).
   */
  get requiresReceipt(): boolean {
    const type = this.editable ? this.selectedType : this.expense?.expenseType;
    return type?.requiresReceipt ?? false;
  }

  get showKm(): boolean {
    return this.selectedType?.type === ExpenseTypeKind.KmExpense;
  }

  get showAmount(): boolean {
    return this.selectedType?.type === ExpenseTypeKind.CashExpense
      && (this.selectedType?.flatrate == null);
  }

  statusName(status: ExpenseStatus): string {
    return ExpenseStatus[status];
  }

  statusClass(status: ExpenseStatus): string {
    return 'status-' + ExpenseStatus[status].toLowerCase();
  }

  save() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const request = {
      expenseTypeId: this.formGroup.value.expenseTypeId,
      description: this.formGroup.value.description,
      km: this.showKm ? this.formGroup.value.km : null,
      amount: this.showAmount ? this.formGroup.value.amount : null,
      linkedAssignmentId: this.formGroup.value.linkedAssignmentId ?? null,
      date: this.toDateOnly(this.formGroup.value.date)
    };

    if (this.currentId) {
      this.expenseStore.updateExpense({ ...request, id: this.currentId }, () => this.changed.emit());
    } else {
      this.expenseStore.createExpense(request, (id) => {
        this.currentId = id;
        this.changed.emit();
      });
    }
  }

  submit() {
    if (!this.currentId) {
      this.close.emit();
      return;
    }
    // Close only once the submit completed, so a parent list reloads the new status (and a failed
    // submit keeps the dialog open).
    this.expenseStore.submitExpense(this.currentId, () => {
      this.changed.emit();
      this.close.emit();
    });
  }

  // "title | internal number | client" of the linked assignment, skipping parts the assignment lacks.
  get linkedAssignmentLabel(): string {
    const e = this.expense;
    return [e?.linkedAssignmentTitle, e?.linkedAssignmentInternalNumber, e?.linkedAssignmentClientName]
      .filter(part => !!part)
      .join(' | ');
  }

  cancel() {
    this.close.emit();
  }

  // --- decide a still-submitted expense (reviewer only) ---

  /** Whether accept/reject actions are available for a submitted (not yet decided) expense. */
  get canDecide(): boolean {
    return this.allowDecide && !this.editable && !!this.expense
      && this.expense.status === ExpenseStatus.Submitted
      // Nobody may decide on their own expense unless they hold ApproveSelf (mirrors the backend rule).
      && (this.expense.submitterId !== this.currentEmployeeId || this.hasApproveSelf);
  }

  // --- reprocess a already-decided expense (reviewer only) ---

  /** Whether reprocess actions are available for the loaded expense. */
  get canReprocess(): boolean {
    return this.allowReprocess && !this.editable && !!this.expense
      && (this.expense.status === ExpenseStatus.Accepted || this.expense.status === ExpenseStatus.Rejected)
      // Nobody may decide on their own expense unless they hold ApproveSelf (mirrors the backend rule).
      && (this.expense.submitterId !== this.currentEmployeeId || this.hasApproveSelf);
  }

  /** Accept a previously rejected expense (no reason required). */
  acceptExpense() {
    if (!this.currentId) {
      this.close.emit();
      return;
    }
    // Close only after the change completed, so the parent list reloads fresh data.
    this.expenseStore.approveExpense(this.currentId, () => this.finishReprocess());
  }

  openReject() {
    this.reasonOpen = true;
  }

  onReasonConfirm(reason: string) {
    this.reasonOpen = false;
    if (!this.currentId) {
      this.close.emit();
      return;
    }
    this.expenseStore.rejectExpense({ id: this.currentId, rejectionReason: reason }, () => this.finishReprocess());
  }

  private finishReprocess() {
    this.changed.emit();
    this.close.emit();
  }

  closeReason() {
    this.reasonOpen = false;
  }
}
